/**
 * ENG-8465: gate measured occupancy on transcript evidence of real work.
 *
 * The pane-occupancy sampler (`pane-occupancy-sampler.ts`) measures DURATION
 * from `pane.log` mtime, which advances on boots, `Auto-updating…` spinner
 * frames and day-rollover respawns as readily as on a model turn. Measured on
 * the prod agent `ledger` (zero completed tasks in four weeks) that overhead
 * billed ~3.5h/week. A read-time output-token FLOOR was rejected: busy
 * tool-heavy agents legitimately emit near-zero output tokens per HOUR, so no
 * hour-grain floor separates them from boot overhead (the ticket's backtest).
 *
 * The discriminator that does separate them is the merged, sabotage-tested
 * qualifier in `@augmented/core` (`extractQualifyingTurns` / `isBucketQualified`,
 * ENG-8465 sibling PR): a wall-clock minute counts only if a real assistant turn
 * (>= DEFAULT_MIN_OUTPUT_TOKENS output tokens, not a synthetic/error record) sits
 * within ±DEFAULT_NEIGHBOURHOOD_MS of it. That keeps a genuine one-off reply and
 * a long tool call (qualified by the turn that LAUNCHED it, within the
 * neighbourhood) while dropping ledger's 2-token boot turns.
 *
 * This module is the host-side wiring that primitive lacked. It:
 *   1. reads qualifying-turn timestamps from the agent's transcripts — the
 *      project dir's top-level *.jsonl AND `<sessionId>/subagents/*.jsonl`
 *      (AC3: a delegation-heavy parent goes static while subagents work,
 *      ENG-6294; scheduled tasks and direct chat each mint their own session);
 *   2. reads a BOUNDED TAIL of each, not the whole file — token-usage-monitor
 *      re-reads whole transcripts (267ms on 45MB), fine at its 60s cadence but
 *      not as a per-drain input. Recent turns are at the end of an append-only
 *      JSONL; a truncated first line is skipped by extractQualifyingTurns. A
 *      per-file {mtime,size} fingerprint skips unchanged files, mirroring
 *      token-usage-monitor;
 *   3. partitions drained buckets and — in `enforce` mode only — drops the
 *      unqualified ones. `shadow` (default) reports everything unchanged and
 *      only COUNTS, so the per-agent impact is measured before the flip.
 *
 * FAIL-OPEN is load-bearing. The core docblock warns that a parse failure
 * "degrades to fewer qualifying turns, which under-bills … silently". So when NO
 * transcript could be read for an agent, this does NOT gate — it reports every
 * bucket and records an `unreadable` signal. Only a SUCCESSFUL read with no
 * qualifying turn near a bucket drops it. Under-billing a real agent because its
 * transcript was briefly unreadable is the failure this exists to avoid, not
 * cause.
 *
 * ENG-8571: fail-open also covers a subtler gap the `unreadable` signal missed.
 * The bounded tail reads only the last TAIL_BYTES of each transcript; for an
 * agent writing faster than ~TAIL_BYTES / TRANSCRIPT_MTIME_WINDOW_MS (~132 B/s),
 * that tail spans LESS than the gate window, so qualifying turns older than the
 * tail are physically not read — yet the read still "succeeds" (`complete` stays
 * true). Dropping a bucket whose neighbourhood the tail never reached would
 * silently under-bill a busy agent (measured in prod: 8 of 22 active agents,
 * 3–23 min each). So `collectQualifyingTurns` reports HOW FAR BACK its reads
 * actually covered (`coveredFromMs`), and `qualifyBuckets` only drops a bucket
 * whose neighbourhood lies fully within that coverage; a bucket reaching before
 * it is kept and counted `coverage_shortfall`, never dropped. A slow-writing,
 * boots-only agent (ledger) is untruncated → fully covered → still gated, so the
 * over-count fix is preserved while busy agents are protected.
 */

import { closeSync, openSync, readdirSync, readSync, statSync } from 'node:fs';
import { join } from 'node:path';

import {
  DEFAULT_NEIGHBOURHOOD_MS,
  extractQualifyingTurns,
  extractToolCallEvents,
  isBucketInBracket,
  isBucketInOpenCall,
  isBucketQualified,
  pairToolCallBrackets,
} from '@augmented/core';
import type { OpenToolCall, ToolCallBracket, ToolCallEvent } from '@augmented/core';

import type { BusyBucket } from './busy-bucket-ledger.js';
import { sessionTranscriptDir } from './daily-session.js';
import { getProjectDir } from './persistent-session.js';

export type OccupancyQualifierMode = 'shadow' | 'enforce';

/**
 * ENG-9061: may an agent whose drain came back EMPTY be reported as
 * "measured, and it was zero" — the roster that stands the legacy
 * `sample_agent_activity()` cron down for it?
 *
 * Three conditions, and each is load-bearing.
 *
 * `attested` — a pane.log read has succeeded for this agent recently
 * (`hasFreshPaneObservation`). Without it the roster would be built from "we
 * called `drainClosed`", which is true even for an agent with no ledger entry at
 * all: the claim would be byte-identical whether the sampler had run all week or
 * died at boot, while its effect is to switch a billing producer OFF. That
 * trades a detectable over-bill for a silent under-bill, and nobody reports a
 * small invoice.
 *
 * `runtimeRunning` — the manager is actually running this agent. NOT implied by
 * `attested`, which is the subtle part: `stopAgentRuntime` kills the tmux
 * session but leaves the agent in `persistentSessionAgents`, and the torn-down
 * agent's `pane.log` lingers on disk and keeps stat'ing successfully. So a
 * paused, draft, revoked or kill-switched agent stays attested forever while its
 * ledger can never accrue again — permanently rostered, standing the fallback
 * producer down on a measurement that is not happening. `attested` proves the
 * FILE is readable; this proves the METER is running. (CodeRabbit, PR #4639.)
 *
 * `mode === 'enforce'` — under `shadow` the pre-flag billing behaviour IS the
 * specification (the flag's own contract is that shadow "reproduces pre-flag
 * billing exactly"), so an unscoped roster would lower billing on hosts where
 * nothing is meant to be changing, at a moment chosen by the host self-update
 * scheduler rather than by us. Scoping here also makes the existing org-scoped,
 * `sensitive` flag the kill switch, so no second gate is needed.
 *
 * Note the empty drain returns from `takeBuckets` BEFORE the qualifier runs, so
 * `mode` is not otherwise consulted on this path — which is exactly how an
 * unscoped version would reach `shadow` hosts unnoticed.
 */
export function rostersMeasuredZero(
  mode: OccupancyQualifierMode,
  attested: boolean,
  runtimeRunning: boolean,
): boolean {
  return mode === 'enforce' && attested && runtimeRunning;
}

/**
 * How much of each transcript's tail to read. A turn record is ~1–5 KB, so 256
 * KB is hundreds of recent turns — far more than the neighbourhood of any
 * bucket drained in a single probe interval. Bounded so a 45 MB transcript
 * costs a few ms, not 267.
 */
export const TAIL_BYTES = 256 * 1024;

/**
 * Cap on how far back the tail read may scan to align to a record boundary. The
 * tail read starts at `size - TAIL_BYTES`, which usually lands mid-record; we
 * scan back to the newline that STARTS that straddling record so the read begins
 * on a complete record (never a fragment `extractQualifyingTurns` would skip and
 * thereby lose a qualifying turn). A single JSONL record larger than this — a
 * maxed-out generation — cannot be aligned within the bound, so the read is
 * reported incomplete and the caller fails open rather than blocking the poll
 * cycle on an unbounded read.
 */
export const MAX_RECORD_ALIGN_BYTES = 8 * 1024 * 1024;

/**
 * Only buckets younger than this are gated. A bucket older than this reached the
 * drain only via outage-recovery re-crediting (the ledger retains up to ~4h
 * while POSTs fail); we no longer hold transcript tail far enough back to judge
 * it, and dropping measured work we cannot re-verify is the wrong direction. So
 * old buckets pass through ungated. Comfortably larger than any probe interval.
 */
export const GATE_MAX_BUCKET_AGE_MS = 30 * 60_000;

/**
 * ENG-9178: how long a bucket may be DEFERRED waiting for its tool call to
 * close before we give up and drop it.
 *
 * Strictly below {@link GATE_MAX_BUCKET_AGE_MS}, and that ordering is the whole
 * point rather than a tidy coincidence. A deferred bucket is credited back to
 * the ledger and re-judged on the next drain, so it keeps ageing; once it passes
 * the 30-minute mark it stops being gate-eligible and takes the `tooOld` branch,
 * which reports it UNGATED. A cap at or above 30 minutes would therefore hand
 * every wedged tool call a free pass out of the gate — billing it via the exact
 * branch that exists to protect outage re-credits, and doing so silently. Five
 * minutes of margin is more than four drain cycles.
 *
 * The cost of the bound is real and one-directional: a tool call that genuinely
 * runs longer than this loses its earliest minutes. That under-bills, which is
 * the safe direction on a meter, and it is still strictly better than the
 * pre-ENG-9178 behaviour where those minutes were dropped outright.
 */
export const MAX_BRACKET_DEFERRAL_MS = 25 * 60_000;

/**
 * Only read transcripts touched within this window — nothing older can hold a
 * turn in the neighbourhood of a gate-eligible bucket. Bounds per-drain I/O.
 */
const TRANSCRIPT_MTIME_WINDOW_MS = GATE_MAX_BUCKET_AGE_MS + DEFAULT_NEIGHBOURHOOD_MS + 60_000;

interface FileFingerprint {
  mtimeMs: number;
  size: number;
  turns: number[];
  /**
   * The file's own coverage floor (ENG-8571): the earliest ms its tail reached,
   * or null when the whole file was read (fully covered, no floor). Cached so a
   * fingerprint hit reproduces the coverage contribution without re-reading.
   */
  coverageFloorMs: number | null;
  /**
   * ENG-9178: the tool-call events in this tail, in FILE ORDER. Cached with the
   * turns so a fingerprint hit reproduces the same pairing without re-reading —
   * and so re-reading an unchanged file cannot move a bracket's edges.
   */
  events: ToolCallEvent[];
}

/** codeName -> (transcript path -> last extracted turns for that fingerprint). */
const cache = new Map<string, Map<string, FileFingerprint>>();

/**
 * ENG-9178: tool-call brackets carried BETWEEN drains, per agent.
 *
 * Two pieces of state, and neither is derivable from a single tail read:
 *
 *   `open` — per transcript path, the calls whose `tool_use` we have seen and
 *   whose `tool_result` has not arrived. Keyed by PATH because pairing must not
 *   cross files: a sub-agent sidecar shares its parent's `sessionId`, and
 *   letting a delegated worker's `tool_result` close the parent's call would
 *   fabricate a bracket across two independent runtimes. (ENG-8573 measured zero
 *   duplicate `tool_use_id` across files, so this is belt-and-braces — but the
 *   belt is free and the failure it prevents is invisible.)
 *
 *   `closed` — recently completed brackets. Needed because a bucket can close
 *   AFTER the bracket that covers it: the last minute of a 24-minute call is
 *   drained on the drain following the `tool_result`, by which time the pairing
 *   has already consumed the open entry and the tail may no longer hold both
 *   ends. Retained for {@link GATE_MAX_BUCKET_AGE_MS}, which is exactly as long
 *   as any bucket can still be judged.
 *
 * Lost on manager restart, deliberately unfixed: the ledger those buckets live
 * in is in-memory too (`sharedBusyBuckets`), so a restart already discards
 * pending occupancy. Losing carried brackets costs at most an under-bill of the
 * in-flight call, matching the direction every other failure here takes.
 */
interface AgentBracketState {
  open: Map<string, OpenToolCall[]>;
  closed: ToolCallBracket[];
}
const brackets = new Map<string, AgentBracketState>();

/** Test seam. */
export function __resetOccupancyGateCacheForTests(): void {
  cache.clear();
  brackets.clear();
}

interface AlignedTail {
  /** UTF-8 content beginning at a complete record boundary. */
  content: string;
  /**
   * False ONLY when a single record straddling the tail start is larger than
   * `maxAlignBytes` and so cannot be aligned within the bound — a qualifying turn
   * inside it could be lost, so the caller must fail open. I/O errors return null
   * (also fail open); an empty file returns '' with complete=true (gates
   * normally).
   */
  complete: boolean;
  /**
   * True when the read began past the file start — i.e. earlier records are NOT
   * in `content`. The caller then knows the tail does not vouch for buckets older
   * than the tail's first record (ENG-8571 coverage). False when the whole file
   * was read (small file, or a straddling FIRST record aligned to offset 0).
   */
  truncated: boolean;
}

/**
 * Read the tail of `path`, ALIGNED to a record boundary so the first line is a
 * complete JSONL record, not a fragment. Scans back from `size - tailBytes` to
 * the newline that starts the straddling record (bounded by `maxAlignBytes`), so
 * a large leading record — even one FOLLOWED by a small record — is recovered
 * rather than skipped as malformed. Returns null on I/O error.
 */
function readAlignedTail(
  path: string,
  tailBytes: number,
  maxAlignBytes: number,
): AlignedTail | null {
  let fd: number | null = null;
  try {
    const size = statSync(path).size;
    if (size <= 0) return { content: '', complete: true, truncated: false };
    fd = openSync(path, 'r');

    let readStart = Math.max(0, size - tailBytes);
    if (readStart > 0) {
      // The bound is measured FROM readStart: a record beginning up to
      // maxAlignBytes before the tail start is recoverable.
      const scanFloor = Math.max(0, readStart - maxAlignBytes);
      const CHUNK = 64 * 1024;
      let pos = readStart;
      let boundary = -1;
      while (pos > scanFloor) {
        const from = Math.max(scanFloor, pos - CHUNK);
        const len = pos - from;
        const b = Buffer.allocUnsafe(len);
        // A short read leaves stale allocUnsafe bytes that could fabricate a
        // newline — treat it as an I/O failure and fail open.
        if (readSync(fd, b, 0, len, from) !== len) return null;
        const idx = b.lastIndexOf(0x0a); // '\n'
        if (idx !== -1) {
          boundary = from + idx + 1;
          break;
        }
        pos = from;
      }
      if (boundary !== -1) {
        readStart = boundary;
      } else if (scanFloor === 0) {
        // No newline back to the file start — the straddling record is the FIRST
        // record, which begins at offset 0 (a valid boundary).
        readStart = 0;
      } else {
        // A single record larger than maxAlignBytes: cannot align → fail open.
        return { content: '', complete: false, truncated: true };
      }
    }

    const len = size - readStart;
    const buf = Buffer.allocUnsafe(len);
    // A short read would report partial content as complete — fail open instead.
    if (readSync(fd, buf, 0, len, readStart) !== len) return null;
    // `truncated` iff the read did not begin at the file start: earlier records
    // are absent, so the tail cannot vouch for buckets older than its first one.
    return { content: buf.toString('utf-8', 0, len), complete: true, truncated: readStart > 0 };
  } catch {
    return null;
  } finally {
    if (fd != null) {
      try {
        closeSync(fd);
      } catch {
        // ignore
      }
    }
  }
}

/** A benign "not a session dir with subagents" error, not a real scan failure. */
function isAbsentDirError(err: unknown): boolean {
  const code = (err as NodeJS.ErrnoException).code;
  return code === 'ENOENT' || code === 'ENOTDIR';
}

/**
 * The earliest wall-clock ms a truncated tail can vouch for: the ROOT `timestamp`
 * of the first JSONL record that carries one. Two deliberate choices (ENG-8571,
 * CodeRabbit):
 *   - ROOT-only, via JSON.parse, NOT a substring match — a nested `timestamp`
 *     (e.g. inside quoted tool output) could be EARLIER than the record's own and
 *     would understate the floor, which is the dangerous direction: it marks a
 *     bucket "covered" and lets enforce drop it.
 *   - forward-scan — the first aligned record may be a type without a root
 *     `timestamp`; using the first one that HAS a root timestamp only ever raises
 *     the floor (we still cover the earlier records, we just can't date them), so
 *     it stays conservative (fail-open more), never less.
 * Returns null when no record yields a root timestamp, so the caller uses a
 * conservative fallback rather than a fabricated (too-low) floor.
 */
function earliestCoveredMs(content: string): number | null {
  for (const line of content.split('\n')) {
    const trimmed = line.trim();
    if (trimmed.length === 0) continue;
    let record: unknown;
    try {
      record = JSON.parse(trimmed);
    } catch {
      continue; // a fragment or non-JSON line — try the next record
    }
    if (typeof record !== 'object' || record === null) continue;
    const ts = (record as { timestamp?: unknown }).timestamp;
    if (typeof ts !== 'string') continue;
    const ms = Date.parse(ts);
    if (Number.isFinite(ms)) return ms;
  }
  return null;
}

/**
 * Every candidate transcript path for an agent: top-level sessions + subagents.
 * `complete` is false when the collection may have MISSED paths — the top dir is
 * unreadable, or a subagent scan failed for a reason other than "no such dir".
 * An incomplete scan must fail open (a missed transcript could hold the very turn
 * that qualifies a bucket), so this is threaded through to qualifyBuckets.
 */
function candidateTranscriptPaths(dir: string): { paths: string[]; complete: boolean } {
  const paths: string[] = [];
  let top: string[];
  try {
    top = readdirSync(dir);
  } catch {
    // The transcript dir is absent or unreadable — an incomplete view.
    return { paths, complete: false };
  }
  let complete = true;
  for (const name of top) {
    if (name.endsWith('.jsonl')) {
      paths.push(join(dir, name));
      continue;
    }
    // A session directory may carry `<sessionId>/subagents/*.jsonl` (ENG-6294):
    // a delegating parent goes static while its subagents keep producing turns.
    const subDir = join(dir, name, 'subagents');
    let subs: string[];
    try {
      subs = readdirSync(subDir);
    } catch (err) {
      // A missing subagents/ dir (or a plain file named like a session) is
      // normal; any other error means we may have missed subagent turns.
      if (!isAbsentDirError(err)) complete = false;
      continue;
    }
    for (const sub of subs) {
      if (sub.endsWith('.jsonl')) paths.push(join(subDir, sub));
    }
  }
  return { paths, complete };
}

export interface QualifyingTurnsResult {
  /** Ascending, de-duplicated epoch-ms of qualifying turns within the window. */
  turns: number[];
  /** How many transcripts fell inside the mtime window and were read. */
  transcriptsRead: number;
  /**
   * Whether the collection was COMPLETE: the transcript dir was enumerable, every
   * in-window file that was statted read successfully (an empty read counts as
   * success, an I/O error does not), and no subagent scan failed for a reason
   * other than "no such dir". FALSE is the fail-open case — an INCOMPLETE view
   * could be missing the very turn that qualifies a bucket, so we must not drop on
   * it. A reachable dir with `transcriptsRead === 0` (nothing recent) is still
   * COMPLETE — there was nothing to miss — so those buckets gate as unqualified
   * rather than being credited forever. Keeping "incomplete" distinct from
   * "nothing recent" also keeps the `unreadable` metric an honest denominator.
   * CodeRabbit, PR review.
   */
  complete: boolean;
  /**
   * ENG-8571: the earliest wall-clock ms these reads can vouch for. A truncated
   * tail (agent writing faster than the tail spans) does not contain turns older
   * than its first record, so a bucket whose neighbourhood reaches before this
   * cannot be proven unqualified and must not be dropped. It is the MAX over all
   * truncated in-window files of their coverage floors — the most conservative
   * bound, since any one truncated session could hide the qualifying turn.
   * NEGATIVE_INFINITY when nothing was truncated (full coverage: gate normally).
   */
  coveredFromMs: number;
  /**
   * ENG-9178: tool calls seen to COMPLETE — `tool_use` paired to the
   * `tool_result` carrying the same `tool_use_id`. A bucket overlapping one of
   * these is evidenced work even with no qualifying turn in reach, which is the
   * whole point: a 24-minute call writes nothing between its two ends, so its
   * middle is invisible to a 120s turn neighbourhood.
   */
  toolCallBrackets: ToolCallBracket[];
  /**
   * ENG-9178: tool calls opened and not yet closed. NOT a qualification — an
   * open call has no end, so crediting one would bill a wedged MCP server
   * forever. Buckets covered by these are DEFERRED: held back, re-judged next
   * drain, released when the call closes, dropped if it never does.
   */
  openToolCalls: OpenToolCall[];
}

/**
 * Fold this drain's tool-call events into the agent's carried bracket state and
 * return what it now knows. Pairing is per PATH (a sub-agent sidecar must not
 * close its parent's call); the closed set is aggregated across paths, because
 * by then a bracket is just an interval and its origin no longer matters.
 *
 * Called with the events of every in-window transcript, INCLUDING those served
 * from the fingerprint cache — an unchanged file still holds the `tool_use` of a
 * call that is still running, and skipping it would drop the open entry and
 * strand every bucket depending on it.
 */
function foldToolCallBrackets(
  codeName: string,
  eventsByPath: Map<string, ToolCallEvent[]>,
  nowMs: number,
): { toolCallBrackets: ToolCallBracket[]; openToolCalls: OpenToolCall[] } {
  const state: AgentBracketState = brackets.get(codeName) ?? {
    open: new Map<string, OpenToolCall[]>(),
    closed: [],
  };
  const nextOpen = new Map<string, OpenToolCall[]>();
  const openOut: OpenToolCall[] = [];

  // Every path we carry state for OR saw events on this drain. A path that
  // vanished (rotated, archived) keeps its open calls until they expire — the
  // call may well still be running; we simply cannot watch it finish any more.
  const paths = new Set([...state.open.keys(), ...eventsByPath.keys()]);
  for (const path of paths) {
    const carried = state.open.get(path) ?? [];
    const paired = pairToolCallBrackets(eventsByPath.get(path) ?? [], carried);
    state.closed.push(...paired.brackets);
    // Expire calls that have been open longer than we are willing to defer a
    // bucket for. Past that bound their buckets are dropped anyway, so holding
    // the entry only grows the map.
    const live = paired.open.filter((c) => nowMs - c.startMs <= MAX_BRACKET_DEFERRAL_MS);
    if (live.length > 0) {
      nextOpen.set(path, live);
      openOut.push(...live);
    }
  }

  // Retain closed brackets exactly as long as a bucket can still be judged, and
  // de-duplicate: the same call pairs again on every drain where both ends stay
  // inside the tail.
  const keepFrom = nowMs - GATE_MAX_BUCKET_AGE_MS;
  const seen = new Set<string>();
  const closed: ToolCallBracket[] = [];
  for (const b of state.closed) {
    if (b.endMs < keepFrom) continue;
    const key = `${b.id}\u0000${b.startMs}\u0000${b.endMs}`;
    if (seen.has(key)) continue;
    seen.add(key);
    closed.push(b);
  }

  brackets.set(codeName, { open: nextOpen, closed });
  return { toolCallBrackets: closed, openToolCalls: openOut };
}

/**
 * Collect qualifying-turn timestamps for an agent from a bounded tail of each
 * recent transcript. `transcriptDir` is injectable for tests.
 */
export function collectQualifyingTurns(
  codeName: string,
  nowMs: number,
  opts: { transcriptDir?: string; tailBytes?: number; maxAlignBytes?: number } = {},
): QualifyingTurnsResult {
  const dir = opts.transcriptDir ?? sessionTranscriptDir(getProjectDir(codeName));
  const tailBytes = opts.tailBytes ?? TAIL_BYTES;
  const maxAlignBytes = opts.maxAlignBytes ?? MAX_RECORD_ALIGN_BYTES;
  const sinceMs = nowMs - TRANSCRIPT_MTIME_WINDOW_MS;
  const agentCache = cache.get(codeName) ?? new Map<string, FileFingerprint>();
  const nextCache = new Map<string, FileFingerprint>();

  const all = new Set<number>();
  // ENG-9178: tool-call events per path, for the bracket fold below. Populated
  // from cache hits as well as fresh reads.
  const eventsByPath = new Map<string, ToolCallEvent[]>();
  let transcriptsRead = 0;
  // The MAX coverage floor across truncated files — the conservative bound (any
  // one truncated session could hide the qualifying turn). NEGATIVE_INFINITY =
  // nothing truncated, full coverage. (ENG-8571)
  let coveredFromMs = Number.NEGATIVE_INFINITY;
  const raiseCoverageFloor = (floor: number | null): void => {
    if (floor != null && floor > coveredFromMs) coveredFromMs = floor;
  };

  const { paths, complete: scanComplete } = candidateTranscriptPaths(dir);
  let complete = scanComplete;
  for (const path of paths) {
    let st;
    try {
      st = statSync(path);
    } catch (err) {
      // A raced deletion (ENOENT) is benign; any other stat error means we could
      // not judge this file, so the collection is incomplete → fail open.
      if (!isAbsentDirError(err)) complete = false;
      continue;
    }
    if (!st.isFile()) continue;
    if (st.mtimeMs < sinceMs) continue; // too old to hold a relevant turn

    const seen = agentCache.get(path);
    if (seen && seen.mtimeMs === st.mtimeMs && seen.size === st.size) {
      // Unchanged since last read — reuse the extracted turns and coverage floor.
      transcriptsRead += 1;
      nextCache.set(path, seen);
      for (const t of seen.turns) all.add(t);
      raiseCoverageFloor(seen.coverageFloorMs);
      // A cache HIT still contributes events: an unchanged transcript is exactly
      // what a running tool call looks like (a `tool_use` was written, then
      // nothing until the result), so skipping it here would drop the open entry
      // for the call we most need to track.
      eventsByPath.set(path, seen.events);
      continue;
    }

    const aligned = readAlignedTail(path, tailBytes, maxAlignBytes);
    if (aligned === null) {
      // I/O error reading a file we could stat — an incomplete view, so fail
      // open. Do NOT count it as read.
      complete = false;
      continue;
    }
    if (!aligned.complete) {
      // A single record straddling the tail start exceeds maxAlignBytes; we could
      // not recover it, so a qualifying turn inside it may be lost — fail open.
      complete = false;
      continue;
    }
    const content = aligned.content;

    // A truncated tail does not vouch for turns older than its first record.
    // Its floor is that record's root timestamp. If the tail carries no parseable
    // root timestamp the floor is UNKNOWN: use `nowMs` for THIS drain (the most
    // conservative floor — forces fail-open for every gated bucket rather than
    // risk an unprovable drop), but do NOT cache it. Caching `nowMs` would freeze
    // it, so a later drain of the still-unchanged, still-unknown file would reuse
    // the older (smaller) value and wrongly treat newer buckets as covered
    // (CodeRabbit). A fully-read file has no floor (null). (ENG-8571)
    const parsedFloorMs = aligned.truncated ? earliestCoveredMs(content) : null;
    const floorUnknown = aligned.truncated && parsedFloorMs === null;
    const coverageFloorMs = aligned.truncated ? (parsedFloorMs ?? nowMs) : null;
    raiseCoverageFloor(coverageFloorMs);

    transcriptsRead += 1;
    const turns = extractQualifyingTurns(content, { startMs: sinceMs });
    // Deliberately NOT windowed by `sinceMs`. A turn older than the window
    // cannot qualify anything, but a `tool_use` older than it absolutely can —
    // that is the entire case ENG-9178 exists for, a call whose start is far
    // behind the buckets it covers. Bounding the events by the same floor as the
    // turns would re-lose the long calls at the exact length they start to matter.
    const events = extractToolCallEvents(content);
    eventsByPath.set(path, events);
    // Only cache a KNOWN floor (a real record timestamp, safe to reuse while the
    // file is unchanged). An unknown-floor file is left uncached so the next
    // drain re-reads it and recomputes against its own `nowMs`.
    if (!floorUnknown) {
      nextCache.set(path, { mtimeMs: st.mtimeMs, size: st.size, turns, coverageFloorMs, events });
    }
    for (const t of turns) all.add(t);
  }

  cache.set(codeName, nextCache);
  const { toolCallBrackets, openToolCalls } = foldToolCallBrackets(codeName, eventsByPath, nowMs);
  return {
    turns: [...all].sort((a, b) => a - b),
    transcriptsRead,
    complete,
    coveredFromMs,
    toolCallBrackets,
    openToolCalls,
  };
}

export interface QualifyBucketsResult {
  /** Buckets to report. In `shadow` this is every input bucket. */
  kept: BusyBucket[];
  qualified: number;
  unqualifiedDropped: number;
  unreadable: number;
  /**
   * ENG-8571: buckets kept (never dropped) because the bounded tail did not reach
   * back far enough to judge them — their neighbourhood begins before
   * {@link QualifyingTurnsResult.coveredFromMs}. A distinct fail-open outcome, so
   * the metric measures how much billing the tail bound is currently protecting.
   */
  coverageShortfall: number;
  /**
   * ENG-9178: buckets kept because they overlap a COMPLETED tool-call bracket,
   * with no qualifying turn in reach. Counted separately from `qualified` so the
   * flip can be sized against turn evidence and bracket evidence independently —
   * they answer different questions and a single number would hide which one is
   * carrying the fix.
   */
  qualifiedByBracket: number;
  /**
   * ENG-9178: buckets HELD — neither reported nor dropped — because they sit
   * inside a tool call that has not returned yet. Under `enforce` these are in
   * {@link QualifyBucketsResult.deferred} and must be credited back to the
   * ledger; under `shadow` they are also in `kept`, because shadow's contract is
   * that it reproduces pre-flag billing exactly.
   */
  bracketDeferred: number;
  /**
   * ENG-9178: buckets to credit BACK to the ledger and judge again next drain.
   * Empty under `shadow`.
   *
   * This is the mechanism the obvious design lacks. The drain runs every 60s and
   * `drainClosed` is destructive, so a bucket is judged about a minute after its
   * minute ends — for a 24-minute call, roughly twenty minutes before its
   * `tool_result` exists. Testing "is this bucket inside a paired bracket?" at
   * drain time therefore answers `false` for every bucket the fix targets.
   * Deferral is what lets a retrospective fact reach a decision already made:
   * don't decide yet.
   */
  deferred: BusyBucket[];
}

/**
 * Partition drained buckets by transcript qualification. Pure — the transcript
 * read is done by {@link collectQualifyingTurns} and passed in, so the billing
 * rule is unit-testable without a filesystem.
 *
 * `complete === false` (dir unreadable, an I/O error, or a failed subagent scan)
 * is the fail-open case: report everything, count it as `unreadable`, never drop.
 * A COMPLETE collection with no recent transcripts is NOT fail-open — its buckets
 * have no qualifying turn and gate as unqualified. Buckets older than
 * {@link GATE_MAX_BUCKET_AGE_MS} pass through ungated (outage-recovery re-credits
 * we can't re-verify). A bucket the tail did not reach back far enough to judge —
 * its neighbourhood begins before {@link QualifyingTurnsResult.coveredFromMs} — is
 * also kept, counted `coverageShortfall`, never dropped (ENG-8571).
 */
export function qualifyBuckets(
  buckets: BusyBucket[],
  qualifying: QualifyingTurnsResult,
  mode: OccupancyQualifierMode,
  nowMs: number,
  neighbourhoodMs: number = DEFAULT_NEIGHBOURHOOD_MS,
): QualifyBucketsResult {
  const failOpen = !qualifying.complete;
  const kept: BusyBucket[] = [];
  const deferred: BusyBucket[] = [];
  let qualified = 0;
  let unqualifiedDropped = 0;
  let unreadable = 0;
  let coverageShortfall = 0;
  let qualifiedByBracket = 0;
  let bracketDeferred = 0;

  for (const b of buckets) {
    const startMs = Date.parse(b.bucket);
    const parsed = Number.isFinite(startMs);
    const tooOld = parsed && nowMs - startMs > GATE_MAX_BUCKET_AGE_MS;

    if (failOpen || tooOld || !parsed) {
      // Count an unparseable timestamp as `unreadable` too, so the three counts
      // stay a COMPLETE partition of the input (BusyBucket.bucket is always ISO,
      // so this is defence in depth, not an expected path). A too-old bucket is
      // ungated but deliberately uncounted — it is not a qualification outcome.
      if (failOpen || !parsed) unreadable += 1;
      kept.push(b); // ungated
      continue;
    }

    if (isBucketQualified(startMs, qualifying.turns, neighbourhoodMs)) {
      // A found turn is positive evidence and wins regardless of coverage.
      qualified += 1;
      kept.push(b);
      continue;
    }

    // ENG-9178: a COMPLETED tool call covering this minute is positive evidence
    // on the same footing as a turn, and wins over coverage for the same reason:
    // we found the thing, so "we might not have read far enough" is moot. Checked
    // after the turn so `qualified` keeps its pre-ENG-9178 meaning exactly —
    // every bucket a turn would have carried is still counted there, and
    // `qualified_by_bracket` is purely additional.
    if (isBucketInBracket(startMs, qualifying.toolCallBrackets)) {
      qualifiedByBracket += 1;
      kept.push(b);
      continue;
    }

    // No qualifying turn was FOUND. Before dropping, the tail must have actually
    // covered this bucket's neighbourhood — otherwise "no turn found" only means
    // "not read", and dropping it would silently under-bill (ENG-8571). The
    // neighbourhood begins at startMs - neighbourhoodMs; if that precedes the
    // covered region, keep the bucket and count the shortfall.
    if (startMs - neighbourhoodMs < qualifying.coveredFromMs) {
      coverageShortfall += 1;
      kept.push(b); // fail open in BOTH modes — never drop what we couldn't read
      continue;
    }

    // ENG-9178: the minute sits inside a tool call that has NOT returned. Do not
    // decide — defer. Checked AFTER coverage on purpose: a bucket we could not
    // read is one we may never be able to judge, and deferring it would end in a
    // drop at the cap. Fail-open must beat "wait and see".
    //
    // The age bound is what keeps a wedged call at zero. Its buckets are held,
    // re-judged each drain, and dropped once they pass
    // MAX_BRACKET_DEFERRAL_MS — which is below GATE_MAX_BUCKET_AGE_MS precisely
    // so they expire while still gate-eligible, instead of ageing into the
    // `tooOld` branch and being reported ungated.
    if (
      nowMs - startMs <= MAX_BRACKET_DEFERRAL_MS &&
      isBucketInOpenCall(startMs, qualifying.openToolCalls)
    ) {
      bracketDeferred += 1;
      // shadow reproduces pre-flag billing EXACTLY, byte-identical on the wire,
      // so it reports the bucket now and only COUNTS the deferral. That keeps
      // the fix measurable before the flip rather than only after it.
      if (mode === 'shadow') kept.push(b);
      else deferred.push(b);
      continue;
    }

    unqualifiedDropped += 1;
    // shadow: still report it (measure only). enforce: drop it.
    if (mode === 'shadow') kept.push(b);
  }

  return {
    kept,
    qualified,
    unqualifiedDropped,
    unreadable,
    coverageShortfall,
    qualifiedByBracket,
    bracketDeferred,
    deferred,
  };
}
